import { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { AgentStep, ReportLine } from "../runner/types";
import { waitForAi } from "./aiProgress";

const RETRY_ERROR_GRACE_MS = 15_000;

const failedLine = (count: number): ReportLine => ({ textId: "app.agent.report.aiFailed", values: { count } });

export const aiRetryStep: AgentStep = {
    id: "aiRetry",
    product: [GQL.IProductKey.Dms],
    async run(ctx, io) {
        const docs = ctx.documents();
        const failed = ctx.created.documentIds.filter((id) => docs.get(id)?.aiStatus?.status === "error");
        if (failed.length === 0) return { status: "skipped" };

        const answer = await io.ask(
            "app.agent.aiRetry.question",
            [
                { value: "retry", labelId: "app.agent.aiRetry.retry", primary: true },
                { value: "skip", labelId: "app.agent.aiRetry.skip" },
            ],
            { count: failed.length },
        );
        if (answer !== "retry") return { status: "skipped", open: [failedLine(failed.length)] };

        await withThinking(io, "app.agent.aiRetry.thinking", () => ctx.processAi(failed));
        const progressId = io.post({
            kind: "progress",
            textId: "app.agent.aiReading.progress",
            progress: { done: 0, total: failed.length },
        });
        const outcome = await waitForAi(ctx, io, failed, progressId, RETRY_ERROR_GRACE_MS);
        const unread = outcome.failed + outcome.notStarted;
        const open = unread > 0 ? [failedLine(unread)] : [];
        if (outcome.running > 0) open.push({ textId: "app.agent.aiStillRunning", values: { count: outcome.running } });
        if (outcome.stalled) {
            io.post({ kind: "text", textId: "app.agent.aiStillRunning", values: { count: outcome.running } });
        }
        if (outcome.cancelled || outcome.stalled) return { status: "done", open, data: { continuesOnServer: true } };

        io.post({
            kind: "done",
            textId: "app.agent.aiRetry.done",
            values: { done: outcome.done, failed: outcome.failed },
        });
        return {
            status: "done",
            summary: [{ textId: "app.agent.report.aiRetried", values: { count: outcome.done } }],
            open,
        };
    },
};
